import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Alert,
  AlertDescription,
  Button,
} from '@openzeppelin/ui-components';
import { Icon } from '@ascend/shared/components';

// ─── Types ─────────────────────────────────────────────────────────────────────

type KycStatus = 'not_started' | 'in_progress' | 'under_review' | 'approved' | 'rejected';

interface PersonalInfoForm {
  fullName: string;
  dateOfBirth: string;
  nationality: string;
  address: string;
  city: string;
  country: string;
  postalCode: string;
}

// ─── Constants ─────────────────────────────────────────────────────────────────

const INPUT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2';

const STEPS = ['Personal Info', 'Documents', 'Selfie', 'Review & Submit'] as const;

const ID_TYPES = ['Passport', 'National ID', "Driver's License"] as const;
const NATIONALITIES = ['United States', 'United Kingdom', 'Germany', 'France', 'Switzerland', 'Singapore', 'Japan', 'Australia', 'Canada', 'Other'] as const;

// ─── Page ──────────────────────────────────────────────────────────────────────

export function KycPage() {
  const [currentStep, setCurrentStep] = useState(0);
  const [kycStatus, setKycStatus] = useState<KycStatus>('not_started');
  const [selectedIdType, setSelectedIdType] = useState<typeof ID_TYPES[number]>(ID_TYPES[0]);
  const [idUploaded, setIdUploaded] = useState(false);
  const [addressProofUploaded, setAddressProofUploaded] = useState(false);
  const [selfieCaptured, setSelfieCaptured] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rejectionReason] = useState('Document quality too low. Please re-upload a clear photo of your ID.');

  const form = useForm<PersonalInfoForm>({
    defaultValues: {
      fullName: '',
      dateOfBirth: '',
      nationality: NATIONALITIES[0],
      address: '',
      city: '',
      country: NATIONALITIES[0],
      postalCode: '',
    },
  });

  const formValues = useWatch({ control: form.control });

  function handleNext() {
    if (currentStep === 0) {
      form.handleSubmit(() => {
        setCurrentStep((s) => Math.min(s + 1, STEPS.length - 1));
        if (kycStatus === 'not_started') setKycStatus('in_progress');
      })();
    } else {
      setCurrentStep((s) => Math.min(s + 1, STEPS.length - 1));
    }
  }

  function handleBack() {
    setCurrentStep((s) => Math.max(s - 1, 0));
  }

  function handleSubmit() {
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setKycStatus('under_review');
      toast.success('KYC submission received! Under review.');
    }, 2000);
  }

  function handleResubmit() {
    setKycStatus('in_progress');
    setCurrentStep(1);
    setIdUploaded(false);
    setAddressProofUploaded(false);
    toast.warning('Please re-upload your documents.');
  }

  const isRejected = kycStatus === 'rejected';
  const isUnderReview = kycStatus === 'under_review';
  const isApproved = kycStatus === 'approved';
  const showForm = !isUnderReview && !isApproved;

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Page header */}
      <div className="flex items-start gap-3">
        <Icon name="streamline:interface-user-check-actions-close-checkmark-check-geometric-human-person-single-success-up-user" className="mt-1 h-6 w-6 sm:h-7 sm:w-7 text-foreground flex-shrink-0" />
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">KYC Verification</h1>
          <p className="text-sm text-muted-foreground">Complete your identity verification to access credit facilities.</p>
        </div>
      </div>

      {/* Step indicator bar */}
      <div className="rounded-xl border border-border p-5 sm:p-6">
        <div className="flex items-center justify-between">
          {STEPS.map((step, i) => {
            const isDone = i < currentStep || isUnderReview || isApproved;
            const isCurrent = i === currentStep && showForm && !isRejected;
            return (
              <div key={step} className="flex flex-1 items-center">
                <div className="flex flex-col items-center flex-1">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${
                    isDone ? 'bg-primary text-primary-foreground' : isCurrent ? 'bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2 ring-offset-background' : 'bg-muted text-muted-foreground'
                  }`}>
                    {isDone ? <Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-4 w-4" /> : i + 1}
                  </div>
                  <span className={`mt-2 text-xs font-medium text-center hidden sm:block ${isDone || isCurrent ? 'text-foreground' : 'text-muted-foreground'}`}>
                    {step}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`h-0.5 w-full mx-1 ${isDone ? 'bg-primary' : 'bg-muted'}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Rejected alert */}
      {isRejected && (
        <Alert>
          <Icon name="streamline:warning-triangle" className="h-4 w-4" />
          <AlertDescription>
            <strong>Verification Rejected:</strong> {rejectionReason}
            <div className="mt-2">
              <Button size="sm" onClick={handleResubmit}>Re-submit Documents</Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Under review state */}
      {isUnderReview && (
        <Card>
          <CardHeader className="p-6">
            <CardTitle>Submission Under Review</CardTitle>
            <CardDescription>Your KYC documents have been submitted and are being reviewed.</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex flex-col items-center gap-4 py-8">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-yellow-100">
                <Icon name="streamline:interface-time-clock-circle-clock-loading-measure-time-circle" className="h-8 w-8 text-yellow-600" />
              </div>
              <h3 className="text-lg font-semibold">Review in Progress</h3>
              <p className="text-sm text-muted-foreground text-center max-w-md">
                Our compliance team is reviewing your submission. This typically takes 1-3 business days. You&apos;ll receive a notification once the review is complete.
              </p>
              <div className="mt-2 rounded-lg bg-muted px-4 py-2 text-xs text-muted-foreground">
                Submitted on {new Date().toLocaleDateString()}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Approved state */}
      {isApproved && (
        <Card>
          <CardHeader className="p-6">
            <CardTitle>Verification Approved</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex flex-col items-center gap-4 py-8">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                <Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold">KYC Approved</h3>
              <p className="text-sm text-muted-foreground text-center max-w-md">
                Your identity has been verified. You can now proceed to access credit facilities.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Multi-step form */}
      {showForm && !isRejected && (
        <>
          {/* Step label */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Step {currentStep + 1}</span> of {STEPS.length}
            <span className="text-muted-foreground">: {STEPS[currentStep]}</span>
          </div>

          <Card>
            <CardHeader className="p-6">
              <CardTitle>{STEPS[currentStep]}</CardTitle>
              <CardDescription>
                {currentStep === 0 && 'Enter your personal information as it appears on your official ID.'}
                {currentStep === 1 && 'Upload required identity documents for verification.'}
                {currentStep === 2 && 'Take a selfie to verify your identity.'}
                {currentStep === 3 && 'Review your information before submitting.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {/* Step 1: Personal Info */}
              {currentStep === 0 && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label className="mb-1.5 block text-sm font-medium">Full Name <span className="text-red-500">*</span></label>
                    <input
                      {...form.register('fullName', { required: true })}
                      placeholder="John Doe"
                      className={INPUT_CLASS}
                    />
                    {form.formState.errors.fullName && <span className="mt-1 text-xs text-red-500">Required</span>}
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium">Date of Birth <span className="text-red-500">*</span></label>
                    <input
                      type="date"
                      {...form.register('dateOfBirth', { required: true })}
                      className={INPUT_CLASS}
                    />
                    {form.formState.errors.dateOfBirth && <span className="mt-1 text-xs text-red-500">Required</span>}
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium">Nationality <span className="text-red-500">*</span></label>
                    <select {...form.register('nationality', { required: true })} className={INPUT_CLASS}>
                      {NATIONALITIES.map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-1.5 block text-sm font-medium">Street Address <span className="text-red-500">*</span></label>
                    <input
                      {...form.register('address', { required: true })}
                      placeholder="123 Main Street"
                      className={INPUT_CLASS}
                    />
                    {form.formState.errors.address && <span className="mt-1 text-xs text-red-500">Required</span>}
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium">City <span className="text-red-500">*</span></label>
                    <input
                      {...form.register('city', { required: true })}
                      placeholder="New York"
                      className={INPUT_CLASS}
                    />
                    {form.formState.errors.city && <span className="mt-1 text-xs text-red-500">Required</span>}
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium">Postal Code</label>
                    <input
                      {...form.register('postalCode')}
                      placeholder="10001"
                      className={INPUT_CLASS}
                    />
                  </div>
                </div>
              )}

              {/* Step 2: Document Upload */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium">ID Type</label>
                    <select
                      value={selectedIdType}
                      onChange={(e) => setSelectedIdType(e.target.value as typeof ID_TYPES[number])}
                      className={`${INPUT_CLASS} sm:w-64`}
                    >
                      {ID_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>

                  <UploadZone
                    label={`Upload ${selectedIdType}`}
                    description="Upload a clear photo of the front of your ID. JPG, PNG, or PDF up to 10MB."
                    uploaded={idUploaded}
                    onUpload={() => { setIdUploaded(true); toast.success(`${selectedIdType} uploaded successfully`); }}
                    onReplace={() => setIdUploaded(false)}
                  />

                  <UploadZone
                    label="Proof of Address"
                    description="Upload a utility bill or bank statement from the last 3 months."
                    uploaded={addressProofUploaded}
                    onUpload={() => { setAddressProofUploaded(true); toast.success('Proof of address uploaded successfully'); }}
                    onReplace={() => setAddressProofUploaded(false)}
                  />
                </div>
              )}

              {/* Step 3: Selfie */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <div className="rounded-xl border-2 border-dashed border-border p-8 sm:p-12 text-center">
                    {selfieCaptured ? (
                      <div className="flex flex-col items-center gap-3">
                        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
                          <Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-10 w-10 text-green-600" />
                        </div>
                        <p className="font-semibold">Selfie Captured</p>
                        <button
                          onClick={() => setSelfieCaptured(false)}
                          className="text-sm text-primary hover:underline"
                        >
                          Retake
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-4">
                        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-muted">
                          <Icon name="streamline:camera-1" className="h-10 w-10 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="font-semibold">Take a Selfie</p>
                          <p className="mt-1 text-sm text-muted-foreground max-w-sm mx-auto">
                            Position your face within the frame. Ensure good lighting and remove sunglasses or hats.
                          </p>
                        </div>
                        <Button onClick={() => { setSelfieCaptured(true); toast.success('Selfie captured successfully'); }}>
                          <Icon name="streamline:camera-1" className="mr-2 h-4 w-4" />
                          Capture Selfie
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Step 4: Review */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <ReviewSection title="Personal Information">
                    <ReviewRow label="Full Name" value={formValues.fullName || '-'} />
                    <ReviewRow label="Date of Birth" value={formValues.dateOfBirth || '-'} />
                    <ReviewRow label="Nationality" value={formValues.nationality ?? '-'} />
                    <ReviewRow label="Address" value={`${formValues.address || '-'}, ${formValues.city || '-'} ${formValues.postalCode || ''}`} />
                  </ReviewSection>

                  <ReviewSection title="Documents">
                    <ReviewRow label="ID Type" value={selectedIdType} />
                    <ReviewRow label="ID Document" value={idUploaded ? 'Uploaded' : 'Not uploaded'} valueClassName={idUploaded ? 'text-green-600' : 'text-red-500'} />
                    <ReviewRow label="Proof of Address" value={addressProofUploaded ? 'Uploaded' : 'Not uploaded'} valueClassName={addressProofUploaded ? 'text-green-600' : 'text-red-500'} />
                  </ReviewSection>

                  <ReviewSection title="Selfie Verification">
                    <ReviewRow label="Selfie" value={selfieCaptured ? 'Captured' : 'Not captured'} valueClassName={selfieCaptured ? 'text-green-600' : 'text-red-500'} />
                  </ReviewSection>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={handleBack} disabled={currentStep === 0}>
              <Icon name="streamline:interface-arrows-button-left-arrow-keyboard-left" className="mr-2 h-4 w-4" />
              Back
            </Button>
            {currentStep < STEPS.length - 1 ? (
              <Button onClick={handleNext}>
                Next
                <Icon name="streamline:interface-arrows-button-right-arrow-right-keyboard" className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={submitting}>
                {submitting ? <><Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="mr-2 h-4 w-4 animate-spin" />Submitting...</> : 'Submit KYC'}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function UploadZone({
  label,
  description,
  uploaded,
  onUpload,
  onReplace,
}: {
  label: string;
  description: string;
  uploaded: boolean;
  onUpload: () => void;
  onReplace: () => void;
}) {
  return (
    <div className="rounded-xl border-2 border-dashed border-border p-6 text-center">
      {uploaded ? (
        <div className="flex flex-col items-center gap-2">
          <Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-8 w-8 text-green-500" />
          <p className="font-medium text-green-700">{label}: Uploaded</p>
          <button type="button" onClick={onReplace} className="text-sm text-primary hover:underline">
            Replace
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <Icon name="streamline:interface-upload-button-1-arrow-button-download-internet-network-server-up-upload" className="h-8 w-8 text-muted-foreground" />
          <div>
            <p className="font-medium">{label}</p>
            <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          </div>
          <Button variant="outline" size="sm" onClick={onUpload}>
            <Icon name="streamline:interface-upload-button-1-arrow-button-download-internet-network-server-up-upload" className="mr-2 h-4 w-4" />
            Choose File
          </Button>
        </div>
      )}
    </div>
  );
}

function ReviewSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border p-5">
      <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold">
        <Icon name="streamline:interface-file-text-text-common-file" className="h-4 w-4 text-muted-foreground" />
        {title}
      </h4>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function ReviewRow({ label, value, valueClassName }: { label: string; value: string; valueClassName?: string }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-medium ${valueClassName ?? ''}`}>{value}</span>
    </div>
  );
}
